import React from 'react';

import { Box, Button, Grid, IconButton, Modal, Tooltip, Typography } from '@mui/material';
import RemoveCircleRoundedIcon from '@mui/icons-material/RemoveCircleRounded';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import CloseIcon from '@mui/icons-material/Close';

import { reportPageStyles as styles } from '../styles';
import { COLOR_GREY_200, COLOR_GREY_600, COLOR_PRIMARY_1 } from 'src/utils/colors';
import { COLOR_GREEN, COLOR_RED } from 'src/utils/const';

const KpiToggleModal = ({
  isOpen,
  onClose,
  title,
  groupedMetrics,
  toggleVisibility,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  groupedMetrics: {
    subsection: string;
    subsectionKey: string | number;
    metrics: { name: string; isVisible: boolean; isAllowed: boolean; key: string }[];
  }[];
  toggleVisibility: any;
}) => {
  const allowedGroupedMetrics = groupedMetrics.map(group => ({
    subsection: group.subsection,
    subsectionKey: group.subsectionKey,
    metrics: group.metrics.filter(kpi => kpi.isAllowed),
  }));

  const unallowedMetrics = groupedMetrics
    .flatMap(group => group.metrics.filter(kpi => !kpi.isAllowed))
    .map(kpi => ({
      name: kpi.name,
    }));

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        sx={{
          ...styles.paper,
          ...styles.modalCenter,
          width: 'clamp(300px, 70%, 600px)',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Grid
          container
          item
          xs={12}
          sx={{
            pt: '15px',
            pl: '30px',
            pr: '30px',
            borderBottom: '1px solid #E0E0E0',
          }}
        >
          <Typography variant="h1" sx={{ mb: 2 }}>
            {title}
          </Typography>

          <IconButton onClick={onClose} sx={styles.toggleListModalCloseIcon}>
            <CloseIcon />
          </IconButton>
        </Grid>

        {/* Scrollable Content */}
        <Box
          sx={{
            overflowY: 'auto',
            flex: 1,
            padding: '30px',
          }}
        >
          <Grid container>
            {allowedGroupedMetrics.map(
              group =>
                group.metrics.length > 0 && (
                  <Grid item xs={12} key={group.subsection} sx={{ mb: 4 }}>
                    <Typography
                      variant="h2"
                      sx={{ color: COLOR_PRIMARY_1, fontWeight: 400, mb: 3 }}
                    >
                      {group.subsection}
                    </Typography>

                    <Grid container direction="column" spacing={1}>
                      {group.metrics.map(kpi => (
                        <Grid
                          container
                          item
                          justifyContent="space-between"
                          alignItems="center"
                          xs={12}
                          sx={{
                            marginBottom: '10px',
                            backgroundColor: '#F3F6FF',
                            borderRadius: '900px',
                            padding: '5px 30px',
                          }}
                        >
                          <Grid container item xs={10}>
                            <Typography
                              variant="body1"
                              sx={{ paddingLeft: 2, marginRight: '10px' }}
                            >
                              {kpi.name}
                            </Typography>
                          </Grid>
                          <Grid container item xs={2} direction="row-reverse">
                            <IconButton
                              onClick={() =>
                                toggleVisibility(
                                  group.subsectionKey,
                                  kpi.key,
                                  kpi.isVisible ? false : true
                                )
                              }
                            >
                              {kpi.isVisible ? (
                                <Tooltip title="Remove" placement="top">
                                  <RemoveCircleRoundedIcon sx={{ color: COLOR_RED }} />
                                </Tooltip>
                              ) : (
                                <Tooltip title="Add" placement="top">
                                  <AddCircleRoundedIcon sx={{ color: COLOR_GREEN }} />
                                </Tooltip>
                              )}
                            </IconButton>
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                )
            )}

            {/* Unavailable Metrics Section */}
            {unallowedMetrics.length > 0 && (
              <Grid item xs={12} sx={{ mb: 4 }}>
                <Typography variant="h2" sx={{ color: COLOR_GREY_600, fontWeight: 400, mb: 3 }}>
                  Unavailable Metrics
                </Typography>

                <Grid container direction="column" spacing={1}>
                  {unallowedMetrics.map(kpi => (
                    <Tooltip title={'Available in different Calculator mode'}>
                      <Grid
                        container
                        item
                        justifyContent="space-between"
                        alignItems="center"
                        xs={12}
                        sx={{
                          marginBottom: '10px',
                          backgroundColor: COLOR_GREY_200,
                          borderRadius: '900px',
                          padding: '5px 30px',
                          opacity: 0.6,
                        }}
                      >
                        <Grid container item xs={10}>
                          <Typography
                            variant="body1"
                            sx={{ paddingLeft: 2, color: COLOR_GREY_600 }}
                          >
                            {kpi.name}
                          </Typography>
                        </Grid>
                        <Grid container item xs={2} direction="row-reverse">
                          <IconButton disabled>
                            <AddCircleRoundedIcon sx={{ color: '#B0B0B0' }} />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Tooltip>
                  ))}
                </Grid>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Sticky Footer */}
        <Box
          sx={{
            position: 'sticky',
            backgroundColor: 'white',
            pt: '15px',
            pb: '15px',
            pl: '30px',
            pr: '30px',
            borderTop: '1px solid #E0E0E0',
            display: 'flex',
            justifyContent: 'flex-end',
            zIndex: 10,
          }}
        >
          <Button sx={styles.actionSectionButton} onClick={onClose}>
            Done
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default KpiToggleModal;
